import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";

import { getErrorMessage } from "services/api";
import { getDiscounts, deleteDiscount } from "features/Discounts/api";
import { DiscountModal } from "features/Discounts/DiscountModal";

import { PageLayout, ActionsRow } from "components/PageLayout";
import { BoldText } from "components/Text";
import { Button } from "components/Button";
import { Table } from "components/Table";
import { SearchInput } from "components/SearchInput";
import { useModal, ConfirmationModal, InfoModal } from "components/Modal";

import styles from "./styles.module.scss";

const modeCell = (params) =>
  params.data?.mode == null
    ? ""
    : Number(params.data.mode) === 1
      ? "Процентная"
      : "Рублёвая";

const valueCell = (params) => {
  const { value, mode } = params.data ?? {};
  if (value == null || value === "") return "";
  return Number(mode) === 1 ? `${Number(value)} %` : `${Number(value)} ₽`;
};

const yesNoCell = (params) => (Number(params.value) === 1 ? "Да" : "Нет");

export const DiscountsPage = () => {
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModal();

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const discountsQuery = useQuery(["discounts"], getDiscounts);
  const discounts = useMemo(
    () => discountsQuery.data?.items ?? [],
    [discountsQuery.data]
  );

  const filtered = useMemo(() => {
    if (!search) return discounts;
    const lower = search.toLowerCase();
    return discounts.filter((d) =>
      [d.name, d.card, d.importcode, d.discountid]
        .map((v) => String(v ?? "").toLowerCase())
        .some((v) => v.includes(lower))
    );
  }, [discounts, search]);

  const colDefs = useMemo(
    () => [
      { field: "discountid", headerName: "Код", width: 90 },
      { field: "name", headerName: "Наименование", flex: 2, minWidth: 220 },
      {
        field: "value",
        headerName: "Величина",
        width: 110,
        cellRenderer: valueCell,
      },
      { field: "mode", headerName: "Вид", width: 120, cellRenderer: modeCell },
      { field: "starttime", headerName: "Действует с", width: 120 },
      { field: "endtime", headerName: "Действует по", width: 120 },
      { field: "card", headerName: "Номер карты", width: 140 },
      { field: "position", headerName: "Позиция", width: 100 },
      {
        field: "avaliable",
        headerName: "Действует",
        width: 110,
        cellRenderer: yesNoCell,
      },
    ],
    []
  );

  const openDiscountModal = (discount) =>
    openModal(<DiscountModal discount={discount} />);

  const onDeleteClick = () => {
    if (!selected) return;
    openModal(
      <ConfirmationModal
        title={`Вы действительно хотите удалить скидку "${selected.name}"?`}
        onConfirm={async () => {
          try {
            await deleteDiscount(selected.discountid);
            setSelected(null);
            queryClient.invalidateQueries(["discounts"]);
            closeModal();
          } catch (e) {
            openModal(
              <InfoModal title={getErrorMessage(e)} onClose={closeModal} />
            );
          }
        }}
        onClose={closeModal}
      />
    );
  };

  return (
    <div className={styles.page}>
      <PageLayout
        header={<BoldText>Скидки</BoldText>}
        footer={
          <ActionsRow>
            <Button icon="plus" onClick={() => openDiscountModal(null)}>
              Добавить скидку
            </Button>
            <Button
              icon="delete"
              onClick={onDeleteClick}
              disabled={!selected}
              title="Удалить скидку"
            />
            <Button
              icon="info"
              onClick={() => openDiscountModal(selected)}
              disabled={!selected}
              title="Свойства скидки"
            />
          </ActionsRow>
        }
        bodyFill
      >
        <div className={styles.searchRow}>
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Поиск"
          />
        </div>
        <Table
          fill
          rowData={filtered}
          colDefs={colDefs}
          rowKey="discountid"
          selectedId={selected?.discountid}
          onSelect={(row) =>
            setSelected(
              discounts.find((d) => d.discountid === row.discountid) || null
            )
          }
          onOpen={(row) => {
            const discount = discounts.find(
              (d) => d.discountid === row.discountid
            );
            if (discount) {
              setSelected(discount);
              openDiscountModal(discount);
            }
          }}
          loading={discountsQuery.isLoading}
          error={
            discountsQuery.isError ? getErrorMessage(discountsQuery.error) : ""
          }
          onRetry={discountsQuery.refetch}
        />
      </PageLayout>
    </div>
  );
};
